<template>
    <div v-if="selectedField" class="field-nav-bar">
        <!-- 顶部案场信息栏 -->
        <div class="field-header" :class="{ 'has-image': selectedField.image }">
            <div :style="{ backgroundImage: `url(${selectedField.image})` }" class="field-thumbnail">
            </div>
        </div>

        <!-- 主选单 (Tab 切换) -->
        <div class="tabs-container">
            <!-- 标签容器 -->
            <div
                class="main-tabs"
                ref="mainTabsContainer"
                @wheel="handleMainTabsWheel"
                @mousedown="handleMainTabsDragStart"
                @mousemove="handleMainTabsDragMove"
                @mouseup="handleMainTabsDragEnd"
                @mouseleave="handleMainTabsDragEnd">
                <button
                    v-for="tab in mainTabs"
                    :key="tab.name"
                    class="tab-button"
                    :class="{ active: currentGroup === tab.group }"
                    @click="handleMainTabClick(tab.name)"
                    :title="tab.title">
                    {{ tab.title }}
                </button>
            </div>
            <!-- 左箭头按钮 -->
            <button
                class="scroll-btn scroll-btn-left"
                :disabled="mainTabsScrollPos <= 0"
                @click="scrollMainTabs(-1)"
                :title="mainTabsScrollPos > 0 ? '向左滚动' : '已到最左'">
                <i class="icofont-simple-left"></i>
            </button>
            <!-- 右箭头按钮 -->
            <button
                class="scroll-btn scroll-btn-right"
                :disabled="mainTabsScrollPos >= mainTabsMaxScroll"
                @click="scrollMainTabs(1)"
                :title="mainTabsScrollPos < mainTabsMaxScroll ? '向右滚动' : '已到最右'">
                <i class="icofont-simple-right"></i>
            </button>
        </div>

        <!-- 副选单 (Sub Tab 切换) -->
        <div v-if="subTabs.length > 0" class="sub-tabs-container">
            <!-- 标签容器 -->
            <div
                class="sub-tabs"
                ref="subTabsContainer"
                @wheel="handleSubTabsWheel"
                @mousedown="handleSubTabsDragStart"
                @mousemove="handleSubTabsDragMove"
                @mouseup="handleSubTabsDragEnd"
                @mouseleave="handleSubTabsDragEnd">
                <button
                    v-for="tab in subTabs"
                    :key="tab.name"
                    class="sub-tab-button"
                    :class="{ active: activeTab === tab.name }"
                    @click="handleSubTabClick(tab.name)"
                    :title="tab.title">
                    {{ tab.title }}
                </button>
            </div>
            <!-- 左箭头按钮 -->
            <button
                class="scroll-btn scroll-btn-left scroll-btn-sub"
                :disabled="subTabsScrollPos <= 0"
                @click="scrollSubTabs(-1)"
                :title="subTabsScrollPos > 0 ? '向左滚动' : '已到最左'">
                <i class="icofont-simple-left"></i>
            </button>
            <!-- 右箭头按钮 -->
            <button
                class="scroll-btn scroll-btn-right scroll-btn-sub"
                :disabled="subTabsScrollPos >= subTabsMaxScroll"
                @click="scrollSubTabs(1)"
                :title="subTabsScrollPos < subTabsMaxScroll ? '向右滚动' : '已到最右'">
                <i class="icofont-simple-right"></i>
            </button>
        </div>
    </div>
</template>

<script>
import { Tooltip, Icon } from 'view-ui-plus';
import { organizationService } from '@shared/services/organization/organizationService';

export default {
    name: 'FieldNavBar',
    components: {
        Tooltip,
        Icon
    },
    props: {
        selectedField: {
            type: Object,
            required: false,
            default: null
        },
        activeTab: {
            type: String,
            default: 'field_FieldDetail'
        }
    },
    emits: ['tab-change'],
    data() {
        return {
            // 滚动相关
            mainTabsScrollPos: 0,
            mainTabsMaxScroll: 0,
            subTabsScrollPos: 0,
            subTabsMaxScroll: 0,
            scrollStep: 150,  // 每次滚动的像素数
            isDraggingMainTabs: false,
            isDraggingSubTabs: false,
            dragStartX: 0,
            dragStartScroll: 0,

            // 主选单配置（从 FullLayout 复制）
            mainTabs: [
                { name: 'field_FieldDetail', title: '基本', group: 'basic_list' },
                { name: 'field_FieldStatistics', title: '系統', group: 'system_list' },
                { name: 'field_FieldEventsList', title: '事件', group: 'event_list' },
                { name: 'field_FieldRiskTrend', title: '風險', group: 'risk_list' },
                { name: 'field_FieldData', title: '數據', group: 'data_list' },
                { name: 'field_FieldEssStrategySettings', title: '策略', group: 'strategy_list' },
                { name: 'field_FieldWorkOrderList', title: '工單', group: 'dispatch_list' },
                { name: 'field_FieldResume', title: '履歷', group: 'resume_list' },
                { name: 'field_FieldContracts', title: '合約', group: 'contract_list' },
                { name: 'field_FieldEquipments', title: '設備', group: 'equipment_list' },
                { name: 'field_FieldDrawings', title: '圖說', group: 'drawing_list' },
                { name: 'field_FieldFiles', title: '檔案', group: 'file_list' },
                { name: 'field_FieldMember', title: '成員', group: 'member_list' },
                { name: 'field_FieldFinance', title: '收支', group: 'finance_list' }
            ],

            // 副选单配置（从 FullLayout 复制）
            allSubTabs: [
                { name: 'field_FieldDetail', title: '基本', group: 'basic_list' },
                { name: 'tab_change_detail_owner', title: '業主', group: 'basic_list' },
                { name: 'tab_change_detail_category', title: '類型', group: 'basic_list' },
                { name: 'tab_change_detail_image', title: '圖片', group: 'basic_list' },

                { name: 'field_FieldStatistics', title: '統計', group: 'system_list' },
                { name: 'field_FieldBillList', title: '帳單', group: 'system_list' },
                { name: 'field_FieldSystem', title: '設定', group: 'system_list' },

                { name: 'field_FieldEventsList', title: '全部', group: 'event_list' },
                { name: 'tab_change_unresolved', title: '未解決', group: 'event_list' },
                { name: 'tab_change_resolved', title: '已解決', group: 'event_list' },
                { name: 'tab_change_unread', title: '未讀', group: 'event_list' },

                { name: 'field_FieldRiskTrend', title: '趨勢', group: 'risk_list' },
                { name: 'tab_change_risk_high', title: '高風險', group: 'risk_list' },
                { name: 'tab_change_risk_medium', title: '中風險', group: 'risk_list' },

                { name: 'field_FieldEssStrategySettings', title: '列表', group: 'strategy_list' },
                { name: 'tab_change_plan', title: '方案', group: 'strategy_list' },
                { name: 'tab_change_strategy', title: '夏季', group: 'strategy_list' },
                { name: 'tab_change_non_summer', title: '非夏季', group: 'strategy_list' },
                { name: 'tab_change_preview', title: '預覽', group: 'strategy_list' },


                { name: 'dispatch_WorkOrderList', title: '列表', group: 'dispatch_list' },
                { name: 'dispatch_DispatchCalendar', title: '日曆', group: 'dispatch_list' },
                { name: 'dispatch_StatisticsPage', title: '統計', group: 'dispatch_list' },
                { name: 'dispatch_WorkReport', title: '回報', group: 'dispatch_list' },

                { name: 'field_FieldData', title: '主控台', group: 'data_list' },
                { name: 'tab_change_edp', title: '調度', group: 'data_list' },
                { name: 'tab_change_ess', title: '儲能櫃', group: 'data_list' },
                { name: 'tab_change_inverter', title: '逆變器', group: 'data_list' },
                { name: 'tab_change_gen', title: '發電機', group: 'data_list' },
                { name: 'tab_change_power_meter', title: '電錶', group: 'data_list' },
                { name: 'tab_change_temp', title: '溫度', group: 'data_list' },
                { name: 'tab_change_pyranometer', title: '日照', group: 'data_list' },
                { name: 'tab_change_pressure', title: '壓力', group: 'data_list' },
                { name: 'tab_change_flow', title: '流量', group: 'data_list' },
                { name: 'tab_change_ipc', title: '電腦', group: 'data_list' },
                { name: 'tab_change_other', title: '其它', group: 'data_list' },

                { name: 'field_FieldResume', title: '全部', group: 'resume_list' },
                { name: 'tab_change_maintenance_resume', title: '保養', group: 'resume_list' },
                { name: 'tab_change_repair_resume', title: '維修', group: 'resume_list' },
                { name: 'tab_change_inspection_resume', title: '巡檢', group: 'resume_list' },
                { name: 'tab_change_installation_resume', title: '安裝', group: 'resume_list' },
                { name: 'tab_change_update_resume', title: '更新', group: 'resume_list' },
                { name: 'tab_change_contract_resume', title: '合約修訂', group: 'resume_list' },
                { name: 'tab_change_other_resume', title: '其他', group: 'resume_list' },

                { name: 'field_FieldContracts', title: '全部', group: 'contract_list' },
                { name: 'tab_change_normal', title: '進行中', group: 'contract_list' },
                { name: 'tab_change_expired', title: '已過期', group: 'contract_list' },

                { name: 'field_FieldEquipments', title: '樹狀', group: 'equipment_list' },
                { name: 'tab_change_card', title: '卡片', group: 'equipment_list' },
                { name: 'tab_change_table', title: '表格', group: 'equipment_list' },

                { name: 'field_FieldDrawings', title: '一般', group: 'drawing_list' },
                { name: 'tab_change_bird', title: '鳥勘', group: 'drawing_list' },
                { name: 'tab_change_map', title: '地圖', group: 'drawing_list' },

                { name: 'field_FieldFiles', title: '全部', group: 'file_list' },
                { name: 'tab_change_image', title: '圖片', group: 'file_list' },
                { name: 'tab_change_video', title: '影片', group: 'file_list' },
                { name: 'tab_change_audio', title: '音檔', group: 'file_list' },
                { name: 'tab_change_pdf', title: 'PDF', group: 'file_list' },

                { name: 'field_FieldMember', title: '全部', group: 'member_list' },
                { name: 'tab_change_owner', title: '擁有者', group: 'member_list' },
                { name: 'tab_change_admin', title: '管理員', group: 'member_list' },
                { name: 'tab_change_viewer', title: '檢視者', group: 'member_list' },
                { name: 'tab_change_editor', title: '編輯者', group: 'member_list' },

                { name: 'field_FieldFinance', title: '全部', group: 'finance_list' },
                { name: 'tab_change_income', title: '定期收入', group: 'finance_list' },
                { name: 'tab_change_expense', title: '定期支出', group: 'finance_list' },
                { name: 'tab_change_maintenance', title: '維運成本', group: 'finance_list' },
                { name: 'tab_change_other_cost', title: '其他費用', group: 'finance_list' }
            ]
        };
    },
    computed: {
        // 获取当前 activeTab 对应的 group
        currentGroup() {
            // 1. 首先尝试在 mainTabs 中找到当前 activeTab
            const currentMainTab = this.mainTabs.find(t => t.name === this.activeTab);
            if (currentMainTab) {
                return currentMainTab.group;
            }

            // 2. 如果在 mainTabs 中找不到，尝试从 allSubTabs 中找到当前 activeTab 对应的 group
            const currentSubTab = this.allSubTabs.find(t => t.name === this.activeTab);
            if (currentSubTab) {
                return currentSubTab.group;
            }

            return null;
        },

        // 根据当前主 tab 获取对应的副选单
        subTabs() {
            const groupName = this.currentGroup;

            if (!groupName) {
                return [];
            }

            return this.allSubTabs.filter(sub => sub.group === groupName);
        },

        // 检查当前案场是否被标记为焦点案场
        isFavorite() {
            if (!this.$root?.view?.currentOrg?.focus_fields) {
                return false;
            }
            return this.$root.view.currentOrg.focus_fields.includes(this.selectedField?.id);
        }
    },
    mounted() {
        // 初始化滚动状态
        this.$nextTick(() => {
            this.updateScrollState();
        });
        window.addEventListener('resize', this.updateScrollState);
    },

    unmounted() {
        window.removeEventListener('resize', this.updateScrollState);
    },

    watch: {
        subTabs() {
            // 当副选单更新时，重新计算滚动状态
            this.$nextTick(() => {
                this.updateScrollState();
            });
        }
    },

    methods: {
        updateScrollState() {
            // 更新主选单滚动状态
            if (this.$refs.mainTabsContainer) {
                const container = this.$refs.mainTabsContainer;
                this.mainTabsMaxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
            }

            // 更新副选单滚动状态
            if (this.$refs.subTabsContainer) {
                const container = this.$refs.subTabsContainer;
                this.subTabsMaxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
            }
        },

        scrollMainTabs(direction) {
            const container = this.$refs.mainTabsContainer;
            if (!container) return;

            const newPos = this.mainTabsScrollPos + direction * this.scrollStep;
            this.mainTabsScrollPos = Math.max(0, Math.min(newPos, this.mainTabsMaxScroll));
            container.scrollLeft = this.mainTabsScrollPos;
        },

        scrollSubTabs(direction) {
            const container = this.$refs.subTabsContainer;
            if (!container) return;

            const newPos = this.subTabsScrollPos + direction * this.scrollStep;
            this.subTabsScrollPos = Math.max(0, Math.min(newPos, this.subTabsMaxScroll));
            container.scrollLeft = this.subTabsScrollPos;
        },

        handleMainTabsWheel(e) {
            e.preventDefault();
            const direction = e.deltaY > 0 ? 1 : -1;
            this.scrollMainTabs(direction);
        },

        handleSubTabsWheel(e) {
            e.preventDefault();
            const direction = e.deltaY > 0 ? 1 : -1;
            this.scrollSubTabs(direction);
        },

        handleMainTabsDragStart(e) {
            this.isDraggingMainTabs = true;
            this.dragStartX = e.clientX;
            this.dragStartScroll = this.mainTabsScrollPos;
            this.$refs.mainTabsContainer.style.cursor = 'grabbing';
        },

        handleMainTabsDragMove(e) {
            if (!this.isDraggingMainTabs) return;

            const delta = this.dragStartX - e.clientX;
            const newPos = this.dragStartScroll + delta;
            this.mainTabsScrollPos = Math.max(0, Math.min(newPos, this.mainTabsMaxScroll));
            this.$refs.mainTabsContainer.scrollLeft = this.mainTabsScrollPos;
        },

        handleMainTabsDragEnd() {
            this.isDraggingMainTabs = false;
            if (this.$refs.mainTabsContainer) {
                this.$refs.mainTabsContainer.style.cursor = 'grab';
            }
        },

        handleSubTabsDragStart(e) {
            this.isDraggingSubTabs = true;
            this.dragStartX = e.clientX;
            this.dragStartScroll = this.subTabsScrollPos;
            this.$refs.subTabsContainer.style.cursor = 'grabbing';
        },

        handleSubTabsDragMove(e) {
            if (!this.isDraggingSubTabs) return;

            const delta = this.dragStartX - e.clientX;
            const newPos = this.dragStartScroll + delta;
            this.subTabsScrollPos = Math.max(0, Math.min(newPos, this.subTabsMaxScroll));
            this.$refs.subTabsContainer.scrollLeft = this.subTabsScrollPos;
        },

        handleSubTabsDragEnd() {
            this.isDraggingSubTabs = false;
            if (this.$refs.subTabsContainer) {
                this.$refs.subTabsContainer.style.cursor = 'grab';
            }
        },

        handleMainTabClick(tabName) {
            this.$emit('tab-change', tabName);
        },

        handleSubTabClick(tabName) {
            // 檢查是否為 tab_change_ 開頭的副選單
            if (tabName.startsWith('tab_change_')) {
                // 找到當前 group
                const currentSubTab = this.allSubTabs.find(t => t.name === tabName);
                if (!currentSubTab) {
                    console.warn('[FieldNavBar] 找不到副選單:', tabName);
                    this.$emit('tab-change', tabName);
                    return;
                }

                const groupName = currentSubTab.group;

                // 找到該 group 對應的主頁面
                const mainTab = this.mainTabs.find(t => t.group === groupName);
                if (!mainTab) {
                    console.warn('[FieldNavBar] 找不到主頁面:', groupName);
                    this.$emit('tab-change', tabName);
                    return;
                }

                // 提取 tab 名稱（去掉 tab_change_ 前綴）
                const tabKey = tabName.replace('tab_change_', '');

                console.log('[FieldNavBar] 處理 tab_change:', {
                    originalName: tabName,
                    groupName: groupName,
                    mainPageName: mainTab.name,
                    tabKey: tabKey
                });

                // 發送切換事件，包含主頁面和 tab 資訊
                this.$emit('tab-change', mainTab.name, { activeTab: tabKey });
            } else {
                // 一般的副選單，直接切換
                this.$emit('tab-change', tabName);
            }
        },

        async handleFocusFieldChange() {
            if (!this.$root?.view?.selectedOrgId || !this.selectedField?.id) return;

            try {
                const currentFocusFields = this.$root.view.currentOrg?.focus_fields || [];
                let newFocusFields = [...currentFocusFields];

                if (!this.isFavorite) {
                    newFocusFields.push(this.selectedField.id);
                } else {
                    newFocusFields = newFocusFields.filter(id => id !== this.selectedField.id);
                }

                await organizationService.updateFocusFields(this.$root.view.currentOrg, newFocusFields);
                this.$Message.success(this.isFavorite ? '已取消焦點案場' : '已設為焦點案場');
            } catch (error) {
                console.error('更新焦點案場失敗:', error);
                this.$Message.error('更新焦點案場失敗');
            }
        }
    }
};
</script>

<style lang="stylus" scoped>
.field-nav-bar
    background: #fff
    border-bottom: 1px solid #ddd
    margin: 0 -10px 10px -10px
    padding: 0

.field-header
    padding: 0
    display: flex
    align-items: center

    .field-info
        display: flex
        align-items: center
        gap: 8px
        z-index: 88

        .favorite-icon
            cursor: pointer
            font-size: 18px
            color: #666
            transition: color 0.3s ease

            &:hover
                color: #f0ad4e

        .field-name
            font-size: 20px
            font-weight: 900
            color: #666666
        &.has-image
            .field-name
                color: #fff
                text-shadow: 0 0 10px rgba(0, 0, 0, 0.5)
            .favorite-icon
                color: #fff
                text-shadow: 0 0 10px rgba(0, 0, 0, 0.5)
                &:hover
                    color: #f0ad4e
                    text-shadow: 0 0 10px rgba(0, 0, 0, 0.5)

// 标签容器（带箭头）

.tabs-container
    margin-top: -3px
.tabs-container, .sub-tabs-container
    display: flex
    align-items: center
    position: relative

    .scroll-btn
        position: absolute
        top: 50%
        transform: translateY(-50%)
        width: 20px
        height: 20px
        padding: 0
        border: none
        background: transparent
        cursor: pointer
        display: flex
        align-items: center
        justify-content: center
        color: #666
        font-size: 14px
        font-weight: bold
        transition: all 0.2s ease
        z-index: 20
        border-radius: 4px

        &:hover:not(:disabled)
            background: rgba(0, 0, 0, 0.1)
            color: #333

        &:active:not(:disabled)
            background: rgba(0, 0, 0, 0.15)

        &:disabled
            cursor: not-allowed
            opacity: 0.3
            color: #999

        &.scroll-btn-left
            left: 0
            background: linear-gradient(to right, rgba(201, 201, 201, 0.9), rgba(201, 201, 201, 0.3), transparent)
            
            &:hover:not(:disabled)
                background: linear-gradient(to right, rgba(201, 201, 201, 1), rgba(201, 201, 201, 0.5), transparent)
            
            &:disabled
                background: linear-gradient(to right, rgba(201, 201, 201, 0.3), rgba(201, 201, 201, 0.1), transparent)

        &.scroll-btn-right
            right: 0
            background: linear-gradient(to left, rgba(201, 201, 201, 0.9), rgba(201, 201, 201, 0.3), transparent)
            
            &:hover:not(:disabled)
                background: linear-gradient(to left, rgba(201, 201, 201, 1), rgba(201, 201, 201, 0.5), transparent)
            
            &:disabled
                background: linear-gradient(to left, rgba(201, 201, 201, 0.3), rgba(201, 201, 201, 0.1), transparent)

        &.scroll-btn-sub
            &.scroll-btn-left
                background: linear-gradient(to right, rgba(240, 248, 255, 0.9), rgba(240, 248, 255, 0.3), transparent)
                
                &:hover:not(:disabled)
                    background: linear-gradient(to right, rgba(240, 248, 255, 1), rgba(240, 248, 255, 0.5), transparent)
                
                &:disabled
                    background: linear-gradient(to right, rgba(240, 248, 255, 0.3), rgba(240, 248, 255, 0.1), transparent)

            &.scroll-btn-right
                background: linear-gradient(to left, rgba(240, 248, 255, 0.9), rgba(240, 248, 255, 0.3), transparent)
                
                &:hover:not(:disabled)
                    background: linear-gradient(to left, rgba(240, 248, 255, 1), rgba(240, 248, 255, 0.5), transparent)
                
                &:disabled
                    background: linear-gradient(to left, rgba(240, 248, 255, 0.3), rgba(240, 248, 255, 0.1), transparent)

// 主选单
.main-tabs
    display: flex
    gap: 2px
    padding: 0 15px
    overflow-x: hidden
    overflow-y: hidden
    flex: 1
    scroll-behavior: smooth
    cursor: grab
    user-select: none
    background: #c9c9c9
    border-top: 1px solid #dddddd

    &::-webkit-scrollbar
        display: none

    .tab-button
        padding: 5px 8px
        border: none
        background: transparent
        cursor: pointer
        font-size: 14px
        font-weight: 500
        height: 38px
        color: #666
        border: 1px solid transparent
        transition: all 0.3s ease
        white-space: nowrap
        margin-top: 2px
        flex-shrink: 0
        border-radius: 4px 4px 0 0

        &:hover
            color: #333
            background: #f5f5f5

        &.active
            color: #0066cc
            border-color: #0066cc #0066cc transparent #0066cc
            background: #f0f8ff
            position: relative
            z-index: 1
            margin-bottom: -1px

// 副选单容器
.sub-tabs-container
    background: #f9f9f9

// 副选单
.sub-tabs
    display: flex
    gap: 2px
    padding: 0 15px
    overflow-x: hidden
    overflow-y: hidden
    background: #f0f8ff
    border-top: 1px solid #0066cc
    margin-top: -2px
    flex: 1
    scroll-behavior: smooth
    cursor: grab
    user-select: none
    position: relative

    &::-webkit-scrollbar
        display: none

    .sub-tab-button
        padding: 2px 8px
        margin-top: 4px
        margin-bottom: 4px
        border: none
        background: transparent
        cursor: pointer
        font-size: 12px
        color: #999
        border: 1px solid transparent
        transition: all 0.3s ease
        white-space: nowrap
        flex-shrink: 0
        border-radius: 4px

        &:hover
            color: #666
            background: #fff

        &.active
            color: #0066cc
            border-color: #0066cc
            background: #fff

.has-image
    position: relative
    overflow: hidden
    border-radius: 4px
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
    height: 150px

.field-thumbnail
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    object-fit: cover
    object-position: center
    background-size: cover
    background-position: center
    background-repeat: no-repeat
    display: block
    z-index: 0
    pointer-events: none
</style>
